import React, { useState } from "react";
import type { MenuProps } from "antd";
import { Menu } from "antd";
import Image from "next/image";
import Link from "next/link";

import { analytics } from "@flare/analytics";
import { useTranslation } from "@flare/i18n";
import { Avatar, Button, cn, Icon, Text } from "@flare/ui";

import {
  PRE_CLIENT_PATH,
  SIGNIN_PAGE_URL,
} from "@/modules/pre-client/pre-client.consts";
import { BiEventsNames } from "@/services/analytics/bi-event-names";

import type { NavbarProps } from "../NavbarV2.types";

type DefaultMenuFooterProps = {
  isPreClientTestEnabled: boolean;
  onSigninClick: () => void;
};

type SignedInMenuFooterProps = {
  onMarbleHomeClick: () => void;
  onSignoutClick: () => void;
};

function DefaultMenuFooter({
  onSigninClick,
  isPreClientTestEnabled,
}: DefaultMenuFooterProps) {
  const { t } = useTranslation();

  const signinURL = isPreClientTestEnabled
    ? SIGNIN_PAGE_URL
    : process.env.NEXT_PUBLIC_CUSTOMERS_APP_URL || "";

  return (
    <div className="mx-sp-6 my-sp-8 flex items-center justify-center gap-sp-2 rounded-sm bg-neutral py-sp-6">
      <Text variant="body1">{t("HAVE_MARBLE_HOME_ACCOUNT")}</Text>
      <Link onClick={onSigninClick} href={signinURL}>
        <Text variant="body1" className="text-action">
          {t("SIGN_IN")}
        </Text>
      </Link>
    </div>
  );
}

function SignedInMenuFooter({
  onMarbleHomeClick,
  onSignoutClick,
}: SignedInMenuFooterProps) {
  const { t } = useTranslation();
  return (
    <div className="mx-sp-6 mb-sp-8 flex flex-col gap-sp-2">
      <Button
        asChild
        endIcon={<Icon name="arrow-right" />}
        size="lg"
        onClick={onMarbleHomeClick}
      >
        <Link href={PRE_CLIENT_PATH}>{t("GO_TO_YOUR_MARBLE_HOME")}</Link>
      </Button>
      <Button size="lg" variant="ghost" onClick={onSignoutClick}>
        {t("SIGN_OUT")}
      </Button>
    </div>
  );
}

export function MobileNavbar({
  items,
  isSignedIn,
  userInitials,
  onMarbleHomeClick,
  onSignoutClick,
  onSigninClick,
  onFunnelCtaClick,
  preClientAbTest,
}: NavbarProps) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState([""]);

  const rootSubmenuKeys = items?.map((item) => item.key);

  function signoutClickHandler() {
    onSignoutClick();
    setIsMenuOpen(false);
  }

  const onOpenChange: MenuProps["onOpenChange"] = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (
      latestOpenKey === undefined ||
      rootSubmenuKeys?.indexOf(latestOpenKey) === -1
    ) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  function toggleMenu() {
    setIsMenuOpen((prevIsOpen) => {
      analytics.track(BiEventsNames.WebPageClick, {
        location: "navbar",
        element_name: "menu",
        click_type: "click",
        click_text: prevIsOpen ? "close" : "open",
      });
      return !prevIsOpen;
    });
  }

  const menuTrigger = isSignedIn ? (
    <Avatar size="md" onClick={toggleMenu} fallback={userInitials || ""} />
  ) : (
    <Icon name="bars" onClick={toggleMenu} />
  );

  return (
    <>
      <nav className="flex h-[56px] items-center justify-between bg-surface px-sp-6">
        <Link href="/">
          <Image
            width={64}
            height={16}
            src="/logos/marble-logo.svg"
            alt={t("MARBLE_LOGO_ALT")}
          />
        </Link>

        <div
          className={cn("flex items-center gap-sp-5", {
            invisible: !preClientAbTest.isReady,
          })}
        >
          {!isSignedIn && (
            <Button size="sm" asChild>
              <Link onClick={onFunnelCtaClick} href="/v2/funnel">
                {t("GET_STARTED")}
              </Link>
            </Button>
          )}
          {isMenuOpen ? (
            <Icon
              name="xmark"
              onClick={toggleMenu}
              className="text-secondary"
            />
          ) : (
            menuTrigger
          )}
        </div>
      </nav>

      {isMenuOpen && (
        <div className="absolute z-10 flex h-[calc(100vh-56px)] w-full flex-col justify-between bg-surface">
          <Menu
            mode="inline"
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            items={items}
          />
          {isSignedIn ? (
            <SignedInMenuFooter
              onMarbleHomeClick={onMarbleHomeClick}
              onSignoutClick={signoutClickHandler}
            />
          ) : (
            <DefaultMenuFooter
              onSigninClick={onSigninClick}
              isPreClientTestEnabled={preClientAbTest.isOn}
            />
          )}
        </div>
      )}
    </>
  );
}
